import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronRight, Download, ArrowUpRight, FileText, CalendarClock, Building2, ShieldCheck, Link as LinkIcon } from 'lucide-react'
import { getCaseStudyData } from '../../data/caseStudies'
import DownloadForm from '../../components/common/DownloadForm'
import CaseStudyDanaFarber from './UseCaseDetail/DFCI'
import CaseStudyCritiCall from './UseCaseDetail/CritiCall'
import CaseStudyLouisianaDOC from './UseCaseDetail/LouisianaDOC'
import CaseStudyLincolnshire from './UseCaseDetail/Lincolnshire'
import CaseStudyNLCHI from './UseCaseDetail/NLCHI'
import CaseStudyCHOC from './UseCaseDetail/CHOC'
import CaseStudySouthernHealth from './UseCaseDetail/SouthernHealth'
import CaseStudyCHIStLukes from './UseCaseDetail/CHIStLukes'
import CaseStudyTelusHealth from './UseCaseDetail/Telus'
import CaseStudyVailHealth from './UseCaseDetail/VailHealth'
import { useNavigationStore } from '@/stores/navigation'

const UseCaseDetail: React.FC = () => {
  const { t } = useTranslation()
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const navigationStore = useNavigationStore()
  
  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  // Special case for DFCI case study - render the dedicated component
  if (slug === 'dfci-cell-therapy') {
    return <CaseStudyDanaFarber />
  }

  // Render synced examples when their slugs match
  if (slug === 'critical-ontario') return <CaseStudyCritiCall />
  if (slug === 'louisiana-doc') return <CaseStudyLouisianaDOC />
  if (slug === 'lincolnshire-each') return <CaseStudyLincolnshire />
  if (slug === 'nlchi-vascular-imaging') return <CaseStudyNLCHI />
  if (slug === 'choc-pediatric-referrals') return <CaseStudyCHOC />
  if (slug === 'southern-health-dermatology') return <CaseStudySouthernHealth />
  if (slug === 'chi-st-lukes-international') return <CaseStudyCHIStLukes />
  if (slug === 'telus-health-corporate') return <CaseStudyTelusHealth />
  if (slug === 'vail-health-covid-programs') return <CaseStudyVailHealth />

  // Get case study data based on slug
  const caseStudy = getCaseStudyData(slug || 'oncology-referrals-referralcx', t)

  const handleBookDemo = () => {
    navigationStore.openDemoModal()
  }

  const handleDownloadPDF = () => {
    setDownloadFormOpen(true)
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-white text-slate-800">
      {/* Breadcrumbs */}
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 text-sm text-slate-500">
        <button 
          onClick={() => navigate('/use-case-finder')}
          className="hover:text-slate-800"
        >
          {t("navigation.useCaseFinder")}
        </button>
        <ChevronRight className="inline mx-1 h-4 w-4" />
        <span className="text-slate-700 font-medium">{caseStudy.client}</span>
      </nav>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(0,84,166,0.12),transparent_70%),radial-gradient(40%_40%_at_90%_10%,rgba(122,192,67,0.12),transparent_70%)]" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10 items-start">
            <div className="lg:col-span-2">
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                {caseStudy.headline}
              </h1>
              <p className="mt-3 max-w-3xl text-slate-600">
                {caseStudy.summary}
              </p>

              {/* Meta row */}
              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                  Industry: {caseStudy.industry}
                </span>
                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                  Region: {caseStudy.region}
                </span>
                <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">
                  Product: {caseStudy.product}
                </span>
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800">
                  Integrations: {caseStudy.integrations.join(' • ')}
                </span>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button 
                  onClick={handleDownloadPDF}
                  className="inline-flex items-center rounded-2xl bg-[#6A11CB] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#5A0FB8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6A11CB]"
                >
                  <Download className="mr-2 h-4 w-4" /> Download full PDF
                </button>
                <button 
                  onClick={handleBookDemo}
                  className="inline-flex items-center rounded-2xl border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6A11CB]"
                >
                  {t("common.bookDemo")} <ArrowUpRight className="ml-2 h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Quick facts (sticky on desktop) */}
            <aside className="lg:sticky lg:top-6">
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-base font-semibold text-gray-900">Quick facts</h3>
                </div>
                <div className="px-6 py-4 space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <Building2 className="mt-0.5 h-4 w-4 text-slate-500" />
                    <div>
                      <div className="font-medium">Organization</div>
                      <div className="text-slate-600">{caseStudy.client}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CalendarClock className="mt-0.5 h-4 w-4 text-slate-500" />
                    <div>
                      <div className="font-medium">Go‑live</div>
                      <div className="text-slate-600">{caseStudy.goLiveDate}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-4 w-4 text-slate-500" />
                    <div>
                      <div className="font-medium">Compliance</div>
                      <div className="text-slate-600">SOC 2 Type 2 / SOC 3 • GDPR aligned (DPF) • HIPAA</div>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 my-3"></div>
                  <div className="grid grid-cols-3 gap-2">
                    {caseStudy.metrics.map((m, i) => (
                      <div key={i} className="rounded-xl bg-slate-50 p-3 text-center">
                        <div className="text-xl font-semibold text-slate-900">{m.value}</div>
                        <div className="text-[11px] text-slate-500">{m.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-200 my-3"></div>
                  <button className="w-full inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6A11CB]">
                    <FileText className="mr-2 h-4 w-4" /> View one‑pager
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Key Metrics */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Key metrics</h3>
              </div>
              <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {caseStudy.keyMetrics.map(({ icon: Icon, label, value }, i) => (
                  <div key={i} className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Icon className="h-4 w-4" />
                      {label}
                    </div>
                    <div className="mt-2 text-2xl font-semibold">{value}</div>
                    <div className="mt-1 text-xs text-slate-500">Based on implementation analytics</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Overview */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Overview</h3>
              </div>
              <div className="px-6 py-4 prose prose-slate max-w-none">
                <h4>Context</h4>
                <p>
                  {caseStudy.context}
                </p>
                <h4>Objectives</h4>
                <ul>
                  <li>Reduce administrative burden and fax‑based workflows</li>
                  <li>Improve referral completion and appointment adherence</li>
                  <li>Standardize data capture and visibility across teams</li>
                </ul>
              </div>
            </div>

            {/* Solution */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Solution</h3>
              </div>
              <div className="px-6 py-4 prose prose-slate max-w-none">
                <p>
                  {caseStudy.solution}
                </p>
              </div>
            </div>

            {/* Implementation */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Implementation & integrations</h3>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {caseStudy.integrations.map((tag) => (
                    <span key={tag} className="inline-flex items-center justify-center rounded-xl bg-gray-100 px-3 py-2 text-sm font-medium text-gray-800">
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-slate-600">Go‑live achieved using EceptionistCX best‑practice templates and configurations.</p>
              </div>
            </div>

            {/* Results */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Results</h3>
              </div>
              <div className="px-6 py-4 prose prose-slate max-w-none">
                <ul>
                  {caseStudy.results.map((result, index) => (
                    <li key={index}>{result}</li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500">* Metrics illustrative; replace with validated outcomes or ranges.</p>
              </div>
            </div>

            {/* Timeline */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Timeline</h3>
              </div>
              <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                {caseStudy.timeline.map((s, i) => (
                  <div key={i} className="rounded-xl border border-gray-200 p-3">
                    <div className="text-sm font-medium text-slate-700">{s.t}</div>
                    <div className="text-slate-600 text-sm">{s.d}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quotes */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">What our users say</h3>
              </div>
              <div className="px-6 py-4 space-y-4">
                {caseStudy.quotes.map((item, i) => (
                  <blockquote key={i} className="rounded-xl bg-slate-50 p-4 border border-gray-200 text-slate-700">
                    "{item.q}"
                    <div className="mt-2 text-sm text-slate-500">— {item.a}</div>
                  </blockquote>
                ))}
              </div>
            </div>

            {/* Resources */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Resources</h3>
              </div>
              <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { title: "Download the case study (PDF)", href: "#" },
                  { title: "Product overview: ReferralCX", href: "#" },
                ].map((r, i) => (
                  <a key={i} href={r.href} target="_blank" rel="noreferrer" className="group rounded-xl border border-gray-200 p-4 hover:shadow-sm transition">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-slate-800 group-hover:text-slate-900">{r.title}</div>
                      <LinkIcon className="h-4 w-4 text-slate-400 group-hover:text-slate-500" />
                    </div>
                    <div className="mt-1 text-xs text-slate-500">Opens in a new tab</div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right column placeholder for spacing on mobile; content is sticky in hero */}
          <div className="hidden lg:block" />
        </div>
      </section>

      {/* Related section */}
      <section className="bg-gradient-to-b from-white to-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Related case studies</h2>
            <button 
              onClick={() => navigate('/use-case-finder')}
              className="text-sm text-[#0054A6] hover:underline"
            >
              View all
            </button>
          </div>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1,2,3].map((i) => (
              <div key={i} className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="p-5">
                  <div className="text-xs text-slate-500">Healthcare / US</div>
                  <div className="mt-1 font-medium">Regional Health System — Improved scheduling efficiency</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                      ReferralCX
                    </span>
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800">
                      FHIR
                    </span>
                  </div>
                  <button className="mt-3 px-0 text-[#0054A6] hover:underline inline-flex items-center">
                    Read more <ArrowUpRight className="ml-1 h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="sticky bottom-0 z-40 border-t bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row items-center gap-3 justify-between">
          <div className="text-sm text-slate-700">
            Ready to achieve outcomes like these? <span className="hidden md:inline">Let's talk about your workflows.</span>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={handleBookDemo}
              className="inline-flex items-center rounded-2xl bg-[#6A11CB] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#5A0FB8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6A11CB]"
            >
              {t("common.bookDemo")}
            </button>
            <button 
              onClick={handleDownloadPDF}
              className="inline-flex items-center rounded-2xl border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6A11CB]"
            >
              <Download className="mr-2 h-4 w-4"/>Download PDF
            </button>
          </div>
        </div>
      </section>
      
      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle={`${caseStudy.client} Case Study`}
        pdfPath={`/PDF/CaseStudies/${caseStudy.slug}.pdf`}
      />
    </div>
  )
}

export default UseCaseDetail
